import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { SESSIONS, INTAKE_FIELDS, SUCCESS_MEASURES, stageOf } from "@/data/bridgeData";
import { getToolForActivity } from "@/data/activityTools";
import { Printer, ArrowLeft } from "lucide-react";

function Lines({ n = 4 }) {
  return (
    <div className="mt-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-9 border-b border-stone-300" />
      ))}
    </div>
  );
}

function ScaleRow({ label }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-stone-100 print-section">
      <span className="text-[15px] text-stone-700">{label}</span>
      <div className="flex gap-1.5 shrink-0">
        {Array.from({ length: 10 }).map((_, i) => (
          <span key={i} className="flex h-7 w-7 items-center justify-center rounded-full border border-stone-300 text-xs text-stone-500">
            {i + 1}
          </span>
        ))}
      </div>
    </div>
  );
}

function FieldBlank({ field }) {
  const lines = field.type === "text" ? 1 : Math.max(3, Math.min(field.rows || 3, 8));
  return (
    <div className="mb-4 print-section">
      <div className="text-sm font-medium text-stone-800">{field.label}</div>
      {field.prompts?.map((p) => <p key={p} className="mt-0.5 text-sm italic text-stone-500">{p}</p>)}
      <Lines n={lines} />
    </div>
  );
}

function ToolBlank({ tool }) {
  if (tool.kind === "list") {
    const count = Math.min(tool.min || 3, 10);
    return (
      <div>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="mb-5 rounded-sm border border-stone-200 p-4 print-section print-card">
            <div className="mb-2 text-xs uppercase tracking-wider text-stone-400">{tool.itemLabel || "Item"} {i + 1}</div>
            {tool.fields.map((f) => (
              <div key={f.id} className="mb-3">
                <div className="text-sm text-stone-700">{f.label}</div>
                <Lines n={f.type === "text" ? 1 : 2} />
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return <div>{tool.fields.map((f) => <FieldBlank key={f.id} field={f} />)}</div>;
}

function PackShell({ eyebrow, title, subtitle, children }) {
  return (
    <Layout coachContext>
      <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/coach" className="mb-3 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-brand-primary transition-colors">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <h1 className="font-serif text-4xl font-light text-stone-900">{title}</h1>
          <p className="mt-2 max-w-xl text-stone-600 leading-relaxed">
            A print-ready workbook your client can complete offline. Click download, then choose "Save as PDF" in the print dialog.
          </p>
        </div>
        <button
          data-testid="pack-print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover"
        >
          <Printer size={17} /> Download PDF
        </button>
      </div>

      <div data-testid="pack-document" className="bg-white">
        <section className="print-section border-b border-stone-200 pb-8 mb-10">
          <div className="flex items-center gap-3 mb-6">
            <span className="block h-px w-10 bg-brand-primary" />
            <span className="font-serif text-2xl text-stone-900">Building The Bridge</span>
          </div>
          <div className="label-eyebrow mb-3">{eyebrow}</div>
          <h2 className="font-serif text-5xl font-light text-stone-900 leading-tight">{title}</h2>
          {subtitle && <p className="mt-4 max-w-2xl text-stone-600 leading-relaxed">{subtitle}</p>}
          <p className="mt-4 text-sm text-stone-500">
            Complete this pack in your own time and at your own pace. Handwrite directly onto these pages, then bring it to your next
            session — or send photos or scans back to your coach.
          </p>
        </section>
        {children}
      </div>
    </Layout>
  );
}

function IntakePack() {
  const scales = SUCCESS_MEASURES.filter((m) => m.type === "scale");
  return (
    <PackShell
      eyebrow="Week 0 · Getting started"
      title="Intake & baseline"
      subtitle="Before the journey begins: where you are now, what you're moving toward, and a baseline reading of your confidence and clarity."
    >
      <section className="mb-12">
        <div className="label-eyebrow mb-5">Part 1 · Intake questions</div>
        {INTAKE_FIELDS.map((f) => <FieldBlank key={f.id} field={{ label: f.label, rows: 5 }} />)}
      </section>
      <section className="print-section mb-12">
        <div className="label-eyebrow mb-2">Part 2 · Baseline confidence & clarity</div>
        <p className="mb-4 text-sm text-stone-600">Circle one number per row — 1 means "not at all", 10 means "completely".</p>
        {scales.map((m) => <ScaleRow key={m.id} label={m.label} />)}
      </section>
    </PackShell>
  );
}

function SessionPackBody({ session }) {
  const stage = stageOf(session.stage);
  return (
    <PackShell
      eyebrow={`Session ${session.id} · Week ${session.week} · ${stage.name}`}
      title={session.title}
      subtitle={session.purpose}
    >
      <section className="print-section mb-12">
        <div className="label-eyebrow mb-3">In this session we will</div>
        <ol className="space-y-1.5">
          {session.structure.map((step, i) => (
            <li key={step} className="text-[15px] text-stone-700">
              <span className="mr-2 text-brand-primary">{i + 1}.</span>{step}
            </li>
          ))}
        </ol>
      </section>

      <section className="mb-12">
        <div className="label-eyebrow mb-2">Part 1 · Session reflections</div>
        <p className="mb-6 text-sm text-stone-600">Take your time with these. Rough notes and honest fragments are more useful than polished answers.</p>
        {session.exercises.map((ex, i) => (
          <div key={ex.id} className="mb-8 print-section">
            <h3 className="font-serif text-2xl text-stone-900">{String.fromCharCode(65 + i)}. {ex.title}</h3>
            <ul className="mt-1 mb-1">
              {ex.prompts.map((p) => <li key={p} className="text-sm italic text-stone-500">· {p}</li>)}
            </ul>
            <Lines n={7} />
          </div>
        ))}
      </section>

      <section className="print-break mb-12 border-t border-stone-200 pt-8">
        <div className="label-eyebrow mb-2">Part 2 · Between-session activities</div>
        <p className="mb-6 text-sm text-stone-600">Work through these before the next session. They feed directly into what we build together.</p>
        {session.activities.map((activity, i) => {
          const tool = getToolForActivity(session.id, i);
          return (
            <div key={activity} className="mb-10">
              <h3 className="font-serif text-2xl text-stone-900 print-section">Activity {i + 1}: {tool ? tool.title : activity}</h3>
              <p className="mt-1 mb-4 text-[15px] leading-relaxed text-stone-600 print-section">{tool?.intro || activity}</p>
              {tool ? <ToolBlank tool={tool} /> : <Lines n={6} />}
            </div>
          );
        })}
      </section>

      <section className="print-section mb-10 rounded-sm border border-stone-200 bg-brand-parchment/30 p-6 print-card">
        <div className="label-eyebrow mb-2">Milestone {session.milestone.n} · {session.milestone.title}</div>
        <p className="text-[15px] leading-relaxed text-stone-700">{session.milestone.desc}</p>
      </section>
    </PackShell>
  );
}

export default function SessionPack() {
  const { packId } = useParams();
  if (packId === "intake") return <IntakePack />;
  const session = SESSIONS.find((s) => String(s.id) === packId);
  if (!session) return <Navigate to="/coach" replace />;
  return <SessionPackBody session={session} />;
}
